import { useState } from 'react';
import { Button, Flex, Stack, useTheme } from '@fti/design-system';
import { useUpdateProject } from '../../../api/media-sim/projects';

interface EditableKeyFactsProps {
  projectId: string;
  keyFacts: string | null;
  /** Called after a successful save so the caller can refresh. */
  onSaved?: () => void;
}

/**
 * Shows a project's key facts with an inline edit.
 *
 * Key facts stay editable after a project is registered, but only until a run
 * has generated an article: they are fed to the journalist agent, so once
 * articles exist they were written from these facts and changing them would
 * leave the stored text disagreeing with what produced it.
 *
 * Whether that point has been reached is decided by the API, not here. The UI
 * does not have the execution or simulation data needed to work it out, and
 * duplicating the rule would risk the two disagreeing. So the edit is offered,
 * and a 403 is surfaced with the API's own explanation.
 */
export function EditableKeyFacts({
  projectId,
  keyFacts,
  onSaved,
}: EditableKeyFactsProps) {
  const { theme } = useTheme();
  const { updateProject } = useUpdateProject();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(keyFacts || '');
  const [error, setError] = useState<string | null>(null);
  const [locked, setLocked] = useState(false);
  const [saving, setSaving] = useState(false);
  // ProjectScopeGate fetches the project and exposes no refetch, and it is
  // shared by several subpages, so hold the saved value here rather than
  // widening that component's API.
  const [current, setCurrent] = useState(keyFacts);

  const open = () => {
    setDraft(current || '');
    setError(null);
    setEditing(true);
  };

  const save = async () => {
    if (saving) return;
    setSaving(true);
    setError(null);
    try {
      // Send null rather than an empty string when cleared: key_facts is
      // nullable and "no key facts" is a real state for a project.
      const saved = await updateProject(projectId, {
        key_facts: draft.trim() ? draft : null,
      });
      setCurrent(saved?.key_facts ?? (draft.trim() ? draft : null));
      setEditing(false);
      onSaved?.();
    } catch (err: unknown) {
      const apiErr = err as { status?: number; message?: string };
      if (apiErr?.status === 403) {
        // The API's message explains why, and names the rule.
        setError(apiErr.message || 'Key facts can no longer be edited.');
        setLocked(true);
      } else {
        setError(apiErr?.message || 'Failed to update key facts.');
      }
    } finally {
      setSaving(false);
    }
  };

  const bodyStyle = {
    fontSize: '0.9rem',
    color: theme.colors.text.primary,
    whiteSpace: 'pre-wrap' as const,
  };

  if (!editing) {
    return (
      <Stack gap="sm">
        {current ? (
          <div style={bodyStyle}>{current}</div>
        ) : (
          <p style={{ color: theme.colors.text.secondary, margin: 0 }}>
            No key facts provided for this project.
          </p>
        )}
        {locked ? (
          <p
            style={{
              fontSize: '0.8rem',
              color: theme.colors.text.secondary,
              margin: 0,
            }}
          >
            {error}
          </p>
        ) : (
          <Flex justify="start">
            <Button variant="ghost" size="sm" onClick={open}>
              Edit
            </Button>
          </Flex>
        )}
      </Stack>
    );
  }

  return (
    <Stack gap="sm">
      <textarea
        value={draft}
        onChange={(e) => {
          setDraft(e.target.value);
          setError(null);
        }}
        rows={12}
        disabled={saving}
        aria-label="Key facts"
        placeholder="Key facts a journalist would reasonably know when writing about this company."
        style={{
          width: '100%',
          padding: '8px 10px',
          fontSize: '0.9rem',
          fontFamily: 'inherit',
          borderRadius: '4px',
          border: `1px solid ${
            error
              ? theme.colors.semantic.error.default
              : theme.colors.border.default
          }`,
          color: theme.colors.text.primary,
          resize: 'vertical',
        }}
      />
      {error && (
        <p
          style={{
            fontSize: '0.8rem',
            color: theme.colors.semantic.error.default,
            margin: 0,
          }}
        >
          {error}
        </p>
      )}
      <Flex gap="sm">
        <Button
          variant="solid"
          color="primary"
          size="sm"
          onClick={save}
          loading={saving}
        >
          Save
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setEditing(false);
            setError(null);
          }}
          disabled={saving}
        >
          Cancel
        </Button>
      </Flex>
    </Stack>
  );
}
